// src/pages/products/ManageProductBrandsPage.jsx
import React, { useState, useEffect } from 'react';
import { 
  Tag, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  MoreVertical,
  AlertCircle,
  RefreshCw,
  Save,
  X,
  Building2,
  Image as ImageIcon,
  Upload
} from 'lucide-react';
import { 
  getBrands, 
  createBrand, 
  getBrand, 
  updateBrand, 
  deleteBrand 
} from '../../services/api/brands';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { getImageUrl, handleImageError } from '../../utils/imageUtils';
import styles from './ManageProductBrandsPage.module.css';

const ManageProductBrandsPage = () => {
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { success, error, confirm } = useAlert();
  
  // State management
  const [brands, setBrands] = useState([]);
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  
  // Modal/Form states
  const [showForm, setShowForm] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logo: null
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load brands on component mount
  useEffect(() => {
    fetchBrands();
  }, []);

  // Filter brands when search query or brands change
  useEffect(() => {
    filterBrands();
  }, [brands, searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null);
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchBrands = async () => {
    try {
      setIsLoading(true);
      setLoadingError(null);
      const response = await getBrands();
      setBrands(response.brands || []);
    } catch (err) {
      console.error('Failed to fetch brands:', err);
      setLoadingError(t('errors.somethingWentWrong'));
    } finally {
      setIsLoading(false);
    }
  };

  const filterBrands = () => {
    if (!searchQuery.trim()) {
      setFilteredBrands(brands);
      return;
    }

    const filtered = brands.filter(brand =>
      brand.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (brand.description && brand.description.toLowerCase().includes(searchQuery.toLowerCase()))
    );
    setFilteredBrands(filtered);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = t('brands.nameRequired');
    } else if (formData.name.trim().length < 2) {
      errors.name = t('brands.nameMinLength');
    }

    // Logo validation
    if (formData.logo && formData.logo instanceof File) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      const maxSize = 2 * 1024 * 1024; // 2MB

      if (!allowedTypes.includes(formData.logo.type)) {
        errors.logo = t('brands.invalidLogoType');
      }

      if (formData.logo.size > maxSize) {
        errors.logo = t('brands.logoTooLarge');
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', logo: null });
    setLogoPreview(null);
    setFormErrors({});
    setEditingBrand(null);
  };

  const handleCreateNew = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEdit = async (brandId) => {
    try {
      setIsSubmitting(true);
      const response = await getBrand(brandId);
      const brand = response;
      
      setFormData({
        name: brand.name || '',
        description: brand.description || '',
        logo: null
      });
      
      // Set logo preview if brand has a logo
      if (brand.logo) {
        setLogoPreview(getImageUrl(brand.logo));
      } else {
        setLogoPreview(null);
      }
      
      setEditingBrand(brand);
      setShowForm(true);
      setActiveDropdown(null);
    } catch (err) {
      console.error('Failed to fetch brand details:', err);
      error(t('brands.failedToLoadBrand'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (brandId, brandName) => {
    const confirmed = await confirm(
      t('brands.confirmDelete', { brandName }),
      {
        confirmText: t('common.delete'),
        cancelText: t('common.cancel')
      }
    );

    if (!confirmed) return;

    try {
      await deleteBrand(brandId);
      setBrands(prev => prev.filter(b => b.id !== brandId));
      success(t('brands.brandDeletedSuccess', { brandName }));
    } catch (err) {
      console.error('Failed to delete brand:', err);
      error(t('brands.failedToDelete'));
    }
    setActiveDropdown(null);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    setFormErrors({});

    try {
      const submitData = new FormData();
      submitData.append('name', formData.name.trim());
      
      if (formData.description.trim()) {
        submitData.append('description', formData.description.trim());
      }
      
      if (formData.logo && formData.logo instanceof File) {
        submitData.append('logo', formData.logo);
      }

      if (editingBrand) {
        // Update existing brand
        const response = await updateBrand(editingBrand.id, submitData);
        setBrands(prev => 
          prev.map(b => b.id === editingBrand.id ? response.brand : b)
        );
        success(t('brands.brandUpdatedSuccess', { brandName: response.brand.name }));
      } else {
        // Create new brand
        const response = await createBrand(submitData);
        setBrands(prev => [...prev, response.brand]);
        success(t('brands.brandCreatedSuccess', { brandName: response.brand.name }));
      }

      setShowForm(false);
      resetForm();
    } catch (err) {
      console.error('Failed to save brand:', err);
      if (err.response?.data?.message) {
        setFormErrors({ submit: err.response.data.message });
      } else {
        setFormErrors({ 
          submit: editingBrand 
            ? t('brands.failedToUpdate') 
            : t('brands.failedToCreate') 
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, logo: file }));
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setLogoPreview(e.target.result);
      reader.readAsDataURL(file);
      
      // Clear logo error
      if (formErrors.logo) {
        setFormErrors(prev => ({ ...prev, logo: '' }));
      }
    }
  };

  const handleRemoveLogo = () => {
    setFormData(prev => ({ ...prev, logo: null }));
    setLogoPreview(null);
    // Reset file input
    const fileInput = document.getElementById('logo-upload');
    if (fileInput) fileInput.value = '';
  };

  const handleCancelForm = () => {
    setShowForm(false);
    resetForm();
  };

  const toggleDropdown = (brandId) => {
    setActiveDropdown(activeDropdown === brandId ? null : brandId);
  };

  if (isLoading) {
    return (
      <div className={styles.manageBrandsPage}>
        <div className={styles.pageHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>
              <Building2 className={styles.headerIcon} size={24} />
              <div className={styles.titleContent}>
                <h1>{t('brands.title')}</h1>
                <p>{t('brands.description')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.loadingState}>
          <div className={styles.loadingSpinnerLarge}></div>
          <h3>{t('brands.loadingBrands')}</h3>
          <p>{t('brands.loadingBrandsDesc')}</p>
        </div>
      </div>
    );
  }

  if (loadingError) {
    return (
      <div className={styles.manageBrandsPage}>
        <div className={styles.pageHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>
              <Building2 className={styles.headerIcon} size={24} />
              <div className={styles.titleContent}>
                <h1>{t('brands.title')}</h1>
                <p>{t('brands.description')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.errorState}>
          <AlertCircle className={styles.errorIcon} size={48} />
          <h3>{t('errors.somethingWentWrong')}</h3>
          <p>{loadingError}</p>
          <button onClick={fetchBrands} className={styles.retryButton}>
            <RefreshCw size={16} />
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.manageBrandsPage}>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <Building2 className={styles.headerIcon} size={24} />
            <div className={styles.titleContent}>
              <h1>{t('brands.title')}</h1>
              <p>{t('brands.description')}</p>
            </div>
          </div>
          
          {hasPermission('create_product_brand') && (
            <div className={styles.headerActions}>
              <button onClick={handleCreateNew} className={styles.primaryButton}>
                <Plus size={20} />
                {t('brands.addBrand')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className={styles.pageControls}>
        <div className={styles.searchSection}>
          <div className={styles.searchInputWrapper}>
            <Search className={styles.searchIcon} size={20} />
            <input
              type="text"
              placeholder={t('brands.searchBrands')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>
        </div>
        
        <div className={styles.controlsRight}>
          <div className={styles.resultsInfo}>
            {searchQuery ? (
              <span>{t('brands.filteredResults', { 
                count: filteredBrands.length,
                total: brands.length 
              })}</span>
            ) : (
              <span>{t('brands.totalBrands', { count: brands.length })}</span>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={styles.pageContent}>
        {filteredBrands.length === 0 ? (
          <div className={styles.emptyState}>
            {searchQuery ? (
              <>
                <Search className={styles.emptyIcon} size={48} />
                <h3>{t('brands.noSearchResults')}</h3>
                <p>{t('brands.noSearchResultsDesc')}</p>
                <button 
                  onClick={() => setSearchQuery('')} 
                  className={styles.secondaryButton}
                >
                  {t('common.clearSearch')}
                </button>
              </>
            ) : (
              <>
                <Building2 className={styles.emptyIcon} size={48} />
                <h3>{t('brands.noBrands')}</h3>
                <p>{t('brands.noBrandsDesc')}</p>
                {hasPermission('create_product_brand') && (
                  <button onClick={handleCreateNew} className={styles.primaryButton}>
                    <Plus size={20} />
                    {t('brands.createFirstBrand')}
                  </button>
                )}
              </>
            )}
          </div>
        ) : (
          <div className={styles.brandsGrid}>
            {filteredBrands.map((brand) => (
              <div key={brand.id} className={styles.brandCard}>
                <div className={styles.brandLogo}>
                  {brand.logo ? (
                    <img
                      src={getImageUrl(brand.logo)}
                      alt={brand.name}
                      className={styles.logoImage}
                      onError={(e) => handleImageError(e)}
                    />
                  ) : null}
                  <div className={`${styles.logoPlaceholder} ${brand.logo ? styles.hidden : ''}`}>
                    <ImageIcon size={24} />
                  </div>
                </div>
                
                <div className={styles.brandInfo}>
                  <h3 className={styles.brandName}>{brand.name}</h3>
                  {brand.description && (
                    <p className={styles.brandDescription}>{brand.description}</p>
                  )}
                </div>
                
                <div className={styles.brandActions}>
                  {hasPermission('edit_product_brand') && (
                    <button 
                      onClick={() => handleEdit(brand.id)}
                      className={`${styles.actionButton} ${styles.editButton}`}
                      title={t('common.edit')}
                    >
                      <Edit size={14} />
                    </button>
                  )}
                  
                  {hasPermission('delete_product_brand') && (
                    <div className={styles.dropdownWrapper}>
                      <button
                        className={`${styles.actionButton} ${styles.moreButton}`}
                        onClick={() => toggleDropdown(brand.id)}
                        title={t('common.moreActions')}
                      >
                        <MoreVertical size={14} />
                      </button>
                      
                      {activeDropdown === brand.id && (
                        <div className={styles.dropdownMenu}>
                          <button
                            className={`${styles.dropdownItem} ${styles.deleteItem}`}
                            onClick={() => handleDelete(brand.id, brand.name)}
                          >
                            <Trash2 size={14} />
                            {t('common.delete')}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2>
                {editingBrand ? t('brands.editBrand') : t('brands.createNewBrand')}
              </h2>
              <button onClick={handleCancelForm} className={styles.modalCloseButton}>
                <X size={20} />
              </button>
            </div>

            {formErrors.submit && (
              <div className={styles.errorBanner}>
                <AlertCircle size={20} />
                <p>{formErrors.submit}</p>
              </div>
            )}

            <form onSubmit={handleFormSubmit} className={styles.modalForm}>
              <div className={styles.formGroup}>
                <label htmlFor="name">{t('brands.brandName')} *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t('brands.brandNamePlaceholder')}
                  className={formErrors.name ? 'error' : ''}
                  required
                  autoFocus
                />
                {formErrors.name && <span className={styles.errorText}>{formErrors.name}</span>}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="description">{t('brands.brandDescription')}</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder={t('brands.brandDescriptionPlaceholder')}
                  className={styles.textarea}
                  rows="3"
                />
              </div>

              <div className={styles.formGroup}>
                <label>{t('brands.brandLogo')}</label>
                <div className={styles.logoUploadContainer}>
                  {logoPreview ? (
                    <div className={styles.logoPreview}>
                      <img src={logoPreview} alt="Logo preview" />
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        className={styles.removeLogoButton}
                        title={t('brands.removeLogo')}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className={styles.logoUploadArea}>
                      <Upload size={24} />
                      <span>{t('brands.uploadLogo')}</span>
                    </div>
                  )}
                  
                  <input
                    type="file"
                    id="logo-upload"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleLogoChange}
                    className={styles.fileInput}
                  />
                  
                  {!logoPreview && (
                    <label htmlFor="logo-upload" className={styles.uploadButton}>
                      <Upload size={16} />
                      {t('brands.chooseLogo')}
                    </label>
                  )}
                </div>
                
                <p className={styles.logoHint}>
                  {t('brands.logoHint')}
                </p>
                
                {formErrors.logo && <span className={styles.errorText}>{formErrors.logo}</span>}
              </div>

              <div className={styles.modalActions}>
                <button 
                  type="button" 
                  onClick={handleCancelForm}
                  className={styles.cancelButton}
                >
                  <X size={18} />
                  {t('common.cancel')}
                </button>
                <button 
                  type="submit" 
                  className={styles.submitButton}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <div className={styles.loadingSpinner}></div>
                  ) : (
                    <>
                      <Save size={18} />
                      {editingBrand ? t('brands.updateBrand') : t('brands.createBrand')}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageProductBrandsPage;